import figureFormatter from '@/services/utils/figureFormatter'

const { commaFormat } = figureFormatter()

export const FAVOURABLE_ABOVE = 'above'

export const LINE_MARK = 'line'

export const ACCOUNT_HEAD = 'account'

export const ITEM_HEAD = 'item'

export const headDataOf = (scope, headType) => {
  if (! scope) return null
  if (! scope.heads) return scope
  return scope.heads[headType] || scope.heads[ACCOUNT_HEAD] || null
}

export const BUDGET_COLOR = '#e0e7ff'

export const BUDGET_BORDER_COLOR = '#818cf8'

export const BUDGET_LINE_COLOR = '#667085'

export const ACTUAL_COLOR = '#4338ca'

export const SERIES_COLORS = ['#4f46e5', '#ea580c', '#0d9488', '#ca8a04', '#db2777', '#0284c7']

const AXIS_SCALES = [
  { from: 1000000000, divisor: 1000000000, suffix: 'B' },
  { from: 1000000, divisor: 1000000, suffix: 'M' },
  { from: 1000, divisor: 1000, suffix: 'K' }
]

export const axisScaleOf = (largest) => AXIS_SCALES.find(scale => largest >= scale.from) || { divisor: 1, suffix: '' }

export const formatAxisValue = (value, scale) => {
  if (! value) return '0'
  return `${Number((value / scale.divisor).toFixed(2))}${scale.suffix}`
}

export const assignSeriesColors = (allIds, seriesIds) => {
  const usedSlots = {}
  return seriesIds.reduce((colors, id) => {
    const start = Math.max(allIds.indexOf(id), 0)
    const slot = SERIES_COLORS.map((color, step) => (start + step) % SERIES_COLORS.length).find(index => ! usedSlots[index])
    usedSlots[slot] = true
    colors[id] = SERIES_COLORS[slot]
    return colors
  }, {})
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const MONTHS_IN_YEAR = 12

const yearMonth = (text) => {
  const [month, year] = text.split('-')
  return `${year}-${String(MONTH_NAMES.indexOf(month) + 1).padStart(2, '0')}`
}

const lastDateOf = (month) => {
  const [year, number] = month.split('-').map(Number)
  return `${month}-${String(new Date(year, number, 0).getDate()).padStart(2, '0')}`
}

export const periodBounds = (label) => {
  const parts = label.split(' - ')
  return { first: yearMonth(parts[0]), last: yearMonth(parts[parts.length - 1]) }
}

export const isPeriodInRange = (label, fromDate, toDate) => {
  const bounds = periodBounds(label)
  return bounds.first <= toDate.slice(0, 7) && bounds.last >= fromDate.slice(0, 7)
}

export const isPeriodPartial = (label, fromDate, toDate) => {
  const bounds = periodBounds(label)
  return `${bounds.first}-01` < fromDate || lastDateOf(bounds.last) > toDate
}

export const monthsCovered = (rows) => {
  const [firstYear, firstMonth] = periodBounds(rows[0].label).first.split('-').map(Number)
  const [lastYear, lastMonth] = periodBounds(rows[rows.length - 1].label).last.split('-').map(Number)
  return (lastYear - firstYear) * MONTHS_IN_YEAR + lastMonth - firstMonth + 1
}

export const periodSpan = (rows) => {
  const firstParts = rows[0].label.split(' - ')
  const lastParts = rows[rows.length - 1].label.split(' - ')
  return `${firstParts[0]} to ${lastParts[lastParts.length - 1]}`
}

const withoutYear = (label) => label.replace(/-\d{4}/g, '')

const hasRepeatedPeriod = (rows) => new Set(rows.map(row => withoutYear(row.label))).size < rows.length

const positionPeriods = (rowLists) => {
  const count = Math.max(0, ...rowLists.map(rows => rows.length))
  return Array.from({ length: count }, (value, position) => ({
    label: `Period ${position + 1}`,
    rows: rowLists.map(rows => rows[position] || null)
  }))
}

const alignedLabel = (rows, key) => {
  const labels = rows.filter(row => row).map(row => row.label)
  return labels.every(label => label === labels[0]) ? labels[0] : key
}

export const alignedPeriods = (rowLists) => {
  if (rowLists.some(hasRepeatedPeriod)) return positionPeriods(rowLists)
  const longestFirst = [...rowLists].sort((first, second) => second.length - first.length)
  const keys = [...new Set(longestFirst.flat().map(row => withoutYear(row.label)))]
  return keys.map(key => {
    const rows = rowLists.map(list => list.find(row => withoutYear(row.label) === key) || null)
    return { label: alignedLabel(rows, key), rows }
  })
}

export const roundAmount = (value) => Math.round(value * 100) / 100

export const formatAmount = (value) => {
  if (! value) return '0.00'
  if (value < 0) return `(${commaFormat(Math.abs(value))})`
  return commaFormat(value)
}

export const formatPercent = (value) => {
  if (value < 0) return `(${Math.abs(value).toFixed(2)}%)`
  return `${value.toFixed(2)}%`
}

export const statusOf = (variance) => {
  if (variance > 0) return { name: 'Favourable', class_name: 'bkm-status-favourable', arrow: '▲' }
  if (variance < 0) return { name: 'Adverse', class_name: 'bkm-status-adverse', arrow: '▼' }
  return { name: 'On Budget', class_name: 'bkm-status-neutral', arrow: '' }
}

export const varianceOf = (budget, actual, favourableWhen) => {
  const difference = favourableWhen === FAVOURABLE_ABOVE ? actual - budget : budget - actual
  return roundAmount(difference)
}

export const variancePercent = (variance, budget) => (budget ? variance / Math.abs(budget) * 100 : 0)

export const usedPercent = (budget, actual) => (budget ? actual / Math.abs(budget) * 100 : 0)
